import {
  SecondaryButton,
  IconArrowSortDownward,
  BUTTON_VARIANTS,
} from "@cdkglobal/radial";
import { useUserContext } from "../../../../../contexts/UserContext";
import { DropdownWithTooltip } from "../../../../common/Tooltip";
import {
  resolveDropdownOptions,
  getRecordsBySearchFilter,
  getResolvedDynamicRowDataValue,
  getSelectedOption,
  getTranslatedLabel,
  getTypeAheadRequest,
  getStoreOptionByGroupId,
  getSelectedGroupIdOption,
  dispatchUpdateField,
  getAllowedGroupIds,
} from "../../../helpers";
import { Fragment, useEffect, useState } from "react";
import CopyGroupCodesTable from "./CopyGroupCodesTable";
import { useUIStateContext } from "../../../../../contexts/UIStateContext";
import {
  RecordsSearchFilterResponse,
  GenericSearchResponseObject,
  PrimitiveValueType,
  SaveRecordRequest,
  ToastInfo,
  RenderCopyDialogProps,
  EnumLabels,
} from "../../../../../types";
import { DEFAULT_TOAST, TOAST_MESSAGES } from "../../../constants";
import { extractErrorAndMessage } from "../../../validations";
import {
  GROUP_CODE_DIALOG_FIELDS_SCHEMA,
  GROUP_CODE_TYPE_TYPEAHEAD_CONFIG,
} from "../constants";
import {
  StyledGroupCodesDialog,
  StyledGroupCodesFieldsRow,
  StyledGroupCodesTable,
} from "../../../../../utils/styled";
import { useRefetchRowDataList, useSaveRecord } from "../../../../../hooks";
import { useIntl } from "react-intl";
import { ampli } from "../../../../../ampli";
import { useTranslation } from "react-i18next";

export default function CopyGroupCodesDialog({
  rowDataReducerState,
  dispatch,
}: Readonly<RenderCopyDialogProps>): JSX.Element {
  const intl = useIntl();
  const { t, i18n } = useTranslation();
  const { setLandingPageToast } = useUIStateContext();
  const {
    selected,
    iamUserState,
    setupBaseRequestInfo,
    setupListNavInfoByDepartment,
  } = useUserContext();

  const [isOpen, setIsOpen] = useState(false);
  const [response, setResponse] = useState<RecordsSearchFilterResponse>({
    message: "",
    data: [],
  });
  const [selectedRows, setSelectedRows] = useState<
    GenericSearchResponseObject[]
  >([]);

  const { dialogRowData = {}, rowData } = rowDataReducerState;
  const groupCodesDialogPropertyKeys = Object.keys(
    GROUP_CODE_DIALOG_FIELDS_SCHEMA,
  );
  const sourceStoreKey = groupCodesDialogPropertyKeys[0];
  const sourceGroupKey = groupCodesDialogPropertyKeys[1];
  const targetGroupKey = groupCodesDialogPropertyKeys[2];
  const groupCodeTypeKey = groupCodesDialogPropertyKeys[3];
  const CUSTOM_GROUP_CODES_KEY_TO_ADD = [
    sourceStoreKey,
    sourceGroupKey,
    targetGroupKey,
  ];

  const sourceStoreValue = dialogRowData[sourceStoreKey];
  const groupCodeTypeValue = dialogRowData[groupCodeTypeKey];

  const sourceStore: string | undefined =
    typeof sourceStoreValue === "string" ? sourceStoreValue : undefined;

  const groupCodeType: string | undefined =
    typeof groupCodeTypeValue === "string" ? groupCodeTypeValue : undefined;

  const selectedGroupCodes: string[] = selectedRows
    .map((row) => row.group_code_id)
    .filter((code): code is string => typeof code === "string");

  const getTableHeaderTitle = () => {
    const selectedGroupIdOption = getSelectedGroupIdOption(
      dialogRowData[sourceGroupKey] as string,
      iamUserState.eisGroups.groups,
    );
    return `${intl.formatMessage({
      id: "label.group_codes",
      defaultMessage: "Group Codes",
    })} ${
      selectedGroupIdOption.label === ""
        ? ""
        : `| ${selectedGroupIdOption.label} | Selected Records - ${selectedRows.length}`
    }`;
  };

  const filteredResponseRowData = response.data.filter(
    (item) => item[groupCodeTypeKey] === dialogRowData[groupCodeTypeKey],
  );

  const handleDialogOpen = () => {
    setIsOpen(true);
    setLandingPageToast(DEFAULT_TOAST);
    ampli.importGroupCodesDialogPopup({
      "Open Import Group Codes Dialog Popup": true,
    });
    dispatchUpdateField({
      propertyKey: targetGroupKey,
      propertySubKey: undefined,
      rowIndex: undefined,
      newFieldValue: selected.groupId.value,
      isDialogField: true,
      selectedTabSectionKey: undefined,
      targetFieldKey: targetGroupKey,
      dispatch,
    });
  };

  const handleDialogClose = () => {
    setIsOpen(false);
    setResponse({ message: "", data: [] });
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        dialogRowData: {},
        dialogRowDataErrors: {},
      },
    });
  };

  const getFilteredEnumOrStoreOptions = (
    propertyKey: string,
    enumValues: PrimitiveValueType[] | undefined,
    isStore: boolean | undefined,
    isGroupId: boolean | undefined,
    enumLabels?: EnumLabels,
  ) => {
    const selectedEnumOrStoreOptions =
      enumValues &&
      resolveDropdownOptions({
        enumValues,
        enumLabels,
        iamUserState,
        isStore,
        isGroupId,
        allowedGroupIds: getAllowedGroupIds({
          selected,
          setupListNavInfoByDepartment,
          iamUserState,
        }),
      });
    if (!selectedEnumOrStoreOptions) return [];
    if (propertyKey === sourceGroupKey)
      return selectedEnumOrStoreOptions.filter(
        (option) => option.value !== selected.groupId.value,
      );
    return selectedEnumOrStoreOptions;
  };

  const onSuccessCallback = (response: ToastInfo): void => {
    setLandingPageToast(response);
    refetchRowDataList();
  };

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel: undefined,
    dispatch,
    setTableTitleInfo: undefined,
  });

  const { mutate: saveRecords } = useSaveRecord({
    onSuccessCallback: onSuccessCallback,
  });

  const copyRecords = () => {
    ampli.importButtonClick({
      "Import Group Codes": true,
      ...(sourceStore ? { "Source Store": sourceStore } : {}),
      ...(groupCodeType ? { "Group Code Type": groupCodeType } : {}),
      ...(selectedGroupCodes.length > 0
        ? { "Selected Group Codes": selectedGroupCodes }
        : {}),
    });
    handleDialogClose();
    setLandingPageToast(TOAST_MESSAGES.IMPORTING());
    const updatedRowData = {
      copy_group_codes: selectedRows.map((record) => ({
        ...record,
        company_id: rowData.company_id,
      })),
    };
    const request: SaveRecordRequest = {
      ...setupBaseRequestInfo,
      record: updatedRowData,
    };
    saveRecords(request);
  };

  useEffect(() => {
    const fetchResponse = async () => {
      const storeId = dialogRowData[sourceStoreKey];

      if (storeId) {
        const existingStoreIdKey = GROUP_CODE_TYPE_TYPEAHEAD_CONFIG.storeId;
        const existingStoreIdValue = existingStoreIdKey
          ? (getResolvedDynamicRowDataValue({
              propertyKey: sourceStoreKey,
              dynamicField: existingStoreIdKey,
              rowData: dialogRowData,
            }) as string)
          : undefined;

        const request = getTypeAheadRequest({
          propertyKey: sourceStoreKey,
          typeaheadConfig: GROUP_CODE_TYPE_TYPEAHEAD_CONFIG,
          rowIndex: undefined,
          rowDataReducerState,
          setupBaseRequestInfo,
          existingStoreIdValue,
          iamUserState,
          setupListNavInfoByDepartment: setupListNavInfoByDepartment,
        });

        if (request) {
          const { response } = await getRecordsBySearchFilter(request);
          setResponse(response);
        }
      }
    };

    fetchResponse();
  }, [dialogRowData[sourceStoreKey]]);

  useEffect(() => {
    if (dialogRowData[sourceGroupKey]) {
      const storeValue = getStoreOptionByGroupId({
        selectedGroupId: dialogRowData[sourceGroupKey] as string,
        iamUserState,
      }).value;
      dispatchUpdateField({
        propertyKey: sourceStoreKey,
        propertySubKey: undefined,
        rowIndex: undefined,
        newFieldValue: storeValue,
        isDialogField: true,
        selectedTabSectionKey: undefined,
        targetFieldKey: sourceStoreKey,
        dispatch,
      });
    }
  }, [dialogRowData[sourceGroupKey]]);

  useEffect(() => {
    setSelectedRows([]);
  }, [dialogRowData[groupCodeTypeKey]]);

  useEffect(() => {
    CUSTOM_GROUP_CODES_KEY_TO_ADD.forEach((key) => {
      dispatch({
        type: "ADD_SCHEMA_PROPERTY",
        payload: {
          newPropertyKey: key,
          newProperty: GROUP_CODE_DIALOG_FIELDS_SCHEMA[key],
        },
      });
    });
  }, []);

  return (
    <>
      <SecondaryButton
        icon={<IconArrowSortDownward />}
        dataTestId="import-group-codes-button"
        iconSize={24}
        onClick={handleDialogOpen}
        size="large"
        text={intl.formatMessage({
          id: "button.importGroupCodes",
          defaultMessage: "Import Group Codes",
        })}
        variant="secondary"
      />
      {isOpen && (
        <StyledGroupCodesDialog
          id="import-group-codes"
          dataTestId="import-group-codes"
          buttonsProps={[
            {
              id: "import-group-codes-cancel",
              onClick: handleDialogClose,
              text: intl.formatMessage({
                id: "button.cancel",
                defaultMessage: "Cancel",
              }),
              variant: BUTTON_VARIANTS.TEXT,
            },
            {
              id: "import-group-codes-import",
              onClick: copyRecords,
              text: intl.formatMessage({
                id: "button.import",
                defaultMessage: "Import",
              }),
              variant: BUTTON_VARIANTS.PRIMARY,
              isDisabled: selectedRows.length === 0,
            },
          ]}
          isOpen={isOpen}
          onClose={handleDialogClose}
          title={intl.formatMessage({
            id: "button.importGroupCodes",
            defaultMessage: "Import Group Codes",
          })}
        >
          <StyledGroupCodesFieldsRow>
            {Object.entries(GROUP_CODE_DIALOG_FIELDS_SCHEMA)
              .filter(([, field]) => !field.isHidden)
              .map(
                ([
                  propertyKey,
                  {
                    title,
                    description,
                    isStore,
                    isGroupId,
                    disabled,
                    enum: enumValues,
                    enumLabels,
                    dependentFields,
                  },
                ]) => {
                  const translatedTitle = getTranslatedLabel(
                    propertyKey,
                    "title",
                    title,
                    t,
                    i18n.language,
                  );

                  const inputValue = dialogRowData[propertyKey];

                  const optionsList = getFilteredEnumOrStoreOptions(
                    propertyKey,
                    enumValues,
                    isStore,
                    isGroupId,
                    enumLabels,
                  );
                  const selectedOption = getSelectedOption({
                    inputValue: inputValue as string,
                    optionsList,
                  });
                  const { error, message } = extractErrorAndMessage(
                    propertyKey,
                    undefined,
                    undefined,
                    rowDataReducerState,
                    true,
                  );

                  return (
                    <Fragment key={propertyKey}>
                      <DropdownWithTooltip
                        title={translatedTitle}
                        description={description ?? ""}
                        tooltip={description}
                        propertyKey={propertyKey}
                        rowIndex={undefined}
                        propertySubKey={undefined}
                        selectedOption={selectedOption}
                        optionsList={optionsList}
                        companyKey={undefined}
                        storeKey={undefined}
                        dependentFields={dependentFields}
                        evalMethod={undefined}
                        isRequired={true}
                        isDisabled={disabled || false}
                        error={error}
                        message={message}
                        dependencyRules={undefined}
                        triggerDependencyRule={undefined}
                        isDialogField={true}
                        selectedTabSectionKey={undefined}
                        rowDataReducerState={rowDataReducerState}
                        dispatch={dispatch}
                      />
                    </Fragment>
                  );
                },
              )}
          </StyledGroupCodesFieldsRow>
          <StyledGroupCodesTable>
            <CopyGroupCodesTable
              headerTitle={getTableHeaderTitle()}
              responseRowData={filteredResponseRowData}
              setSelectedRows={setSelectedRows}
            />
          </StyledGroupCodesTable>
        </StyledGroupCodesDialog>
      )}
    </>
  );
}
